/**
 * @copyright 2026 Fortee Uniforme Inc. All rights reserved.
 * @license PROPRIETARY - Unauthorized copying, modification, or distribution
 *          of this file is strictly prohibited. This software is protected
 *          under the Canadian Copyright Act (R.S.C., 1985, c. C-42).
 * @author Fortee Uniforme Inc. <info@fortee.ca>
 *
 * NOTICE: This file contains trade secrets and proprietary information.
 * Reverse engineering, decompilation, or disassembly is prohibited.
 */
// fortee-order-schema.js — the ONE place a cart line's properties are shaped.
//
// WHY THIS FILE EXISTS
// --------------------
// A downstream processor reads Shopify order line-item properties after
// checkout. Before this module the properties differed in two dimensions that
// have nothing to do with the order:
//
//   1. STOREFRONT LOCALE. Property KEYS and VALUES were built through _t(), so
//      the same order placed on /fr/ arrived as `Devant · Illustration`,
//      `Maquette`, `Broderie`, `Poitrine gauche` and `12,7 cm`. Every consumer
//      had to know both languages, or silently missed half the orders.
//   2. WHICH BUILDER MADE THE LINE. The single builder emitted `_print_area_N`
//      and positional geometry triples; the bundle builder emitted neither and
//      folded the spot into the key string (`Artwork Front (Left Chest)`).
//
// The fix is a single flat, snake_case, ENGLISH-ONLY schema emitted through
// this module by both builders. Translation is presentation only: the cart page
// and cart drawer map these canonical keys to French labels via `| t`. The key
// and the value stay English on every storefront, including on Shopify's own
// checkout page and order-confirmation email. That is a deliberate, accepted
// trade (decision: Ugo, 2026-08-20) — the processor is the customer here.
//
// THE RULES BELOW ARE THE DEFECTS, WRITTEN DOWN
// ---------------------------------------------
//   * Numbers are formatted with Number(v).toFixed(1). NEVER through the
//     `decimal_separator` locale key — that is what put "12,7" on French orders.
//   * A placement with no canonical key is DROPPED with a warning. It cannot be
//     normalized, and guessing its location is how a decoration ends up on the
//     wrong part of a garment.
//   * A mockup whose URL starts with `data:` is dropped with a warning. The
//     compositor falls back to a multi-MB base64 string when Uploadcare fails,
//     and that would dump as a wall of text on the cart page.
//   * A view already present in the output never gets a second key. Mockups were
//     previously written TWICE (`_ftmockup_front` and `Mockup front`, same URL).
//   * An absent value OMITS its key. Never '' , never null, never a placeholder.
//
// NOT A PRICING SEAM. This module writes down what the builders decided; it does
// not decide anything about money. The "no placement, no property" guards stay
// in the builders, where the paid set is known (single-builder.js cartSubmit,
// bundle-cart-builder.js restrictToPlacements).

(function (root) {
  'use strict';

  // ---------------------------------------------------------------------------
  // Canonical location names
  // ---------------------------------------------------------------------------
  // Keyed by the print-area metaobject's `system_alias` value, which is the
  // stable internal identifier: sections/main-single-builder.liquid and
  // sections/main-bundle-builder-2step.liquid both bridge `area.system_alias`
  // as the area's `handle`, and scripts/translate-print-areas.py allowlists
  // exactly {display_name} for translation — so the alias is locale-independent
  // BY CONSTRUCTION while `display_name` is not ("Poitrine gauche").
  //
  // Aliases come from the fallback zone tables in bundle-builder.js and
  // bundle-builder-2step.js, which are the closest thing the codebase has to an
  // enumeration of the print areas actually in the catalogue.
  var LOCATION_NAMES = {
    left_chest: 'Left Chest',
    right_chest: 'Right Chest',
    center_chest: 'Center Chest',
    // The catalogue carries both spellings for the same spot. They are one
    // location, and a processor grouping by name must see one name.
    front_center: 'Center Chest',
    upper_chest: 'Upper Chest',
    full_front: 'Full Front',
    // `back` is the historical alias for a full back print — see the fallback
    // table, where `back` and `full_back` differ only in default zone size.
    back: 'Full Back',
    full_back: 'Full Back',
    upper_back: 'Upper Back',
    lower_back: 'Lower Back',
    left_arm: 'Left Sleeve',
    left_sleeve: 'Left Sleeve',
    right_arm: 'Right Sleeve',
    right_sleeve: 'Right Sleeve',
    center_cap: 'Center Cap',
    heart_side_cap: 'Heart Side Cap'
  };

  function isBlank(v) {
    return v === null || v === undefined || String(v).trim() === '';
  }

  function warn(message) {
    if (root && root.console && typeof root.console.warn === 'function') {
      root.console.warn('[FORTEE order-schema] ' + message);
    }
  }

  // Fold the many written forms of one alias onto a single lookup key.
  // Real data arrives as `left-chest`, `Left_Chest`, `left_chest_dtf` and
  // `dawn-standard-left-chest-print` depending on how the metaobject was
  // created; the zone fallback tables already normalise the first three the
  // same way.
  function normalizeAlias(alias) {
    var s = String(alias)
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_+|_+$/g, '');
    s = s.replace(/^dawn_standard_/, '');
    s = s.replace(/_(dtf|print|embroidery)$/, '');
    return s;
  }

  function titleCase(s) {
    return String(s)
      .replace(/[-_]+/g, ' ')
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map(function (w) { return w.charAt(0).toUpperCase() + w.slice(1); })
      .join(' ');
  }

  /**
   * Resolve a print-area alias to its canonical {key, name}.
   * Returns null for a blank alias — the caller drops that placement.
   *
   * An UNKNOWN alias is not an error: the catalogue gains print areas. It is
   * derived and WARNED, so a new area shows up in the console instead of
   * silently shipping a guessed English label nobody reviewed.
   */
  function canonicalLocation(alias) {
    if (isBlank(alias)) return null;
    var key = normalizeAlias(alias);
    if (!key) return null;
    if (Object.prototype.hasOwnProperty.call(LOCATION_NAMES, key)) {
      return { key: key, name: LOCATION_NAMES[key] };
    }
    warn('unknown print-area alias "' + alias + '" — using the derived name "' +
      titleCase(key) + '". Add it to LOCATION_NAMES.');
    return { key: key, name: titleCase(key) };
  }

  /**
   * Resolve a decoration method to `DTF` or `Embroidery`.
   * Matches the French forms too (`Impression`, `Broderie`) so a value that
   * already went through _t() upstream still lands on the canonical token.
   */
  function canonicalMethod(raw) {
    if (isBlank(raw)) return null;
    var s = String(raw).trim();
    if (/dtf|print|impress/i.test(s)) return 'DTF';
    if (/embroid|brod/i.test(s)) return 'Embroidery';
    warn('unrecognised decoration method "' + s + '" — passed through unchanged.');
    return s;
  }

  var VIEW_ALIASES = {
    front: 'front',
    back: 'back',
    left: 'left',
    right: 'right',
    left_side: 'left',
    right_side: 'right',
    // The single builder's gallery calls the default garment shot `model`
    // (single-builder.js:3191). It is the front.
    model: 'front'
  };

  /**
   * Resolve a view token to `front` | `back` | `left` | `right`.
   * Cap views (`cap_front`, `cap_back`, …) fold onto the side they sit on.
   */
  function canonicalView(raw) {
    if (isBlank(raw)) return null;
    var s = String(raw).trim().toLowerCase().replace(/[\s-]+/g, '_');
    if (Object.prototype.hasOwnProperty.call(VIEW_ALIASES, s)) return VIEW_ALIASES[s];
    if (s.indexOf('back') !== -1) return 'back';
    if (s.indexOf('left') !== -1) return 'left';
    if (s.indexOf('right') !== -1) return 'right';
    if (s.indexOf('front') !== -1) return 'front';
    warn('unrecognised view "' + raw + '" — treated as front.');
    return 'front';
  }

  // Write `value` at `key` only when it is actually present. An empty string on
  // an order line is worse than a missing key: it reads as "the customer left
  // this blank" rather than "this line has no such field".
  function put(props, key, value) {
    if (isBlank(value)) return;
    props[key] = String(value);
  }

  // Trim, then keep a value within Shopify's 255-char property limit. A longer
  // value is cut at the last '; ' boundary so a list never ends mid-item.
  function clipAtBoundary(value, max) {
    if (isBlank(value)) return null;
    var s = String(value).trim();
    if (s.length <= max) return s;
    var cut = s.lastIndexOf('; ', max);
    return cut > 0 ? s.slice(0, cut) : s.slice(0, max);
  }

  // ONE decimal, ALWAYS a '.' separator. The locale's decimal_separator is a
  // presentation concern and has no business on an order line.
  function num1(value) {
    var n = Number(value);
    if (!isFinite(n)) return null;
    return n.toFixed(1);
  }

  /**
   * normalizeOrderItem — the single normalization layer.
   *
   * Takes ONE builder-agnostic input shape and returns the flat cart-line
   * properties object. There is deliberately NO builder branching in here: if a
   * builder needs something shaped differently, it adapts on its side, so the
   * two can never drift apart again.
   *
   * @param {Object} item
   * @param {'single'|'bundle'} item.builder
   * @param {number} item.submitTimestamp   epoch ms — rollback matches on it
   * @param {{title: string, url: string}|null} item.bundle
   * @param {{id, handle, name}} item.product
   * @param {{id, sku}} item.variant
   * @param {string} item.color             variant option1 (canonical EN)
   * @param {string} item.size              variant option2 (canonical EN)
   * @param {number} item.quantity
   * @param {string|null} item.note         the per-product customer note
   * @param {boolean} item.artworkSkipped
   * @param {Array<Object>} item.placements {key, view, method,
   *        originalArtworkUrl, productionArtworkUrl, editsLayerUrl,
   *        editsSpec, widthIn, heightIn, xIn, yIn, artInstructions,
   *        brandColors[]}
   * @param {Object} item.mockupsByView     {front: url, back: url, …}
   * @returns {Object} flat, snake_case, English cart-line properties
   */
  function normalizeOrderItem(item) {
    var it = item || {};
    var props = {};

    // --- Plumbing (private: Shopify hides `_`-prefixed keys from the cart page
    // and the checkout, but keeps them on the order in admin) ----------------
    put(props, '_builder', it.builder);
    // Unchanged on purpose: both builders' rollbackPartialCart matches lines by
    // this stamp, and a submit whose lines carry a different one is a submit
    // whose partial failure cannot be undone.
    if (!isBlank(it.submitTimestamp)) props['_submitTimestamp'] = String(it.submitTimestamp);
    if (it.bundle) {
      put(props, '_bundle', it.bundle.title);
      put(props, '_bundle_url', it.bundle.url);
    }

    // --- Item identity -------------------------------------------------------
    // product_handle / product_id / variant_id / sku are all canonical and
    // identify the product unambiguously. product_name is NOT: product titles
    // are on the translation allowlist and Liquid exposes no untranslated
    // title, so a /fr/ order carries a French name here. Documented, not hidden.
    var product = it.product || {};
    var variant = it.variant || {};
    put(props, 'product_id', product.id);
    put(props, 'product_handle', product.handle);
    put(props, 'product_name', product.name);
    put(props, 'variant_id', variant.id);
    put(props, 'sku', variant.sku);
    put(props, 'color', it.color);
    put(props, 'size', it.size);
    put(props, 'quantity', it.quantity);

    // --- Placements ----------------------------------------------------------
    var placements = [];
    var methods = [];
    (it.placements || []).forEach(function (raw) {
      if (!raw) return;
      var loc = canonicalLocation(raw.key);
      if (!loc) {
        warn('placement dropped: no print-area alias' +
          (isBlank(raw.view) ? '' : ' (view "' + raw.view + '")') +
          '. It cannot be normalized, and a guessed location prints in the wrong place.');
        return;
      }
      var method = canonicalMethod(raw.method);
      if (method && methods.indexOf(method) === -1) methods.push(method);
      placements.push({ raw: raw, loc: loc, method: method, view: canonicalView(raw.view) });
    });

    props['placement_count'] = String(placements.length);
    if (methods.length) props['decoration_method'] = methods.join(', ');

    placements.forEach(function (p, idx) {
      var prefix = 'placement_' + (idx + 1) + '_';
      var raw = p.raw;
      props[prefix + 'key'] = p.loc.key;
      props[prefix + 'location'] = p.loc.name;
      put(props, prefix + 'view', p.view);
      put(props, prefix + 'method', p.method);
      put(props, prefix + 'original_artwork_url', raw.originalArtworkUrl);
      put(props, prefix + 'production_artwork_url', raw.productionArtworkUrl);
      // The customer's logo-editor text and shapes: a transparent PNG cropped
      // to the production PNG's own rect (overlays it 1:1) and an English
      // spec. Only a real https url is written — never a data: payload.
      if (typeof raw.editsLayerUrl === 'string' && /^https:\/\//i.test(raw.editsLayerUrl)) {
        put(props, prefix + 'edits_layer_url', raw.editsLayerUrl);
      }
      put(props, prefix + 'edits_spec', clipAtBoundary(raw.editsSpec, 255));

      // Geometry is all-or-nothing and keyed off a real width. A placement the
      // customer never sized contributes NO geometry keys — no default is
      // invented, because an invented size gets printed.
      var w = num1(raw.widthIn);
      if (w !== null && Number(raw.widthIn) > 0) {
        props[prefix + 'logo_width_inches'] = w;
        var h = num1(raw.heightIn);
        if (h !== null && Number(raw.heightIn) > 0) props[prefix + 'logo_height_inches'] = h;
        // 0.0 is a legitimate position; only a non-finite value is absent.
        var x = num1(raw.xIn);
        if (x !== null) props[prefix + 'position_x_inches'] = x;
        var y = num1(raw.yIn);
        if (y !== null) props[prefix + 'position_y_inches'] = y;
      }

      put(props, prefix + 'art_instructions', raw.artInstructions);
      var colors = (raw.brandColors || []).filter(function (c) { return !isBlank(c); });
      if (colors.length) props[prefix + 'brand_colors'] = colors.join(', ');
    });

    // --- Mockups -------------------------------------------------------------
    // A mockup is per VIEW, never per placement: it is one baked garment photo
    // showing every logo on that side. There is deliberately no
    // `placement_<i>_mockup_url` — the processor joins through
    // `placement_<i>_view`. That is what lets "one mockup per view, never
    // repeated" and "every placement is addressable" both be true at once.
    var placementViews = {};
    placements.forEach(function (p) { if (p.view) placementViews[p.view] = true; });
    var mockups = it.mockupsByView || {};
    Object.keys(mockups).forEach(function (rawView) {
      var url = mockups[rawView];
      if (isBlank(url)) return;
      var view = canonicalView(rawView);
      if (!view) return;
      if (String(url).indexOf('data:') === 0) {
        warn('mockup for "' + view + '" is a base64 data URL, not a CDN url — omitted. ' +
          'Check the Uploadcare upload errors above.');
        return;
      }
      if (!placementViews[view]) {
        warn('mockup for "' + view + '" has no placement on this line — omitted ' +
          '(it would show a decoration nobody was charged for).');
        return;
      }
      var key = 'mockup_' + view;
      // A view already present never gets a second key. Writing the same URL
      // twice under two names is exactly what this schema removed.
      if (Object.prototype.hasOwnProperty.call(props, key)) return;
      props[key] = String(url);
    });

    // --- Remaining -----------------------------------------------------------
    // A canonical token, not a sentence: "Customer will send later" /
    // "Le client enverra plus tard" were two spellings of one fact.
    if (it.artworkSkipped) props['artwork_status'] = 'customer_will_send_later';
    put(props, 'customer_note', it.note);

    return props;
  }

  /**
   * placementsWithoutArtwork — the paid placements on a FINISHED property set
   * that have nothing to print.
   *
   * Reports, never refuses. This module writes down what the builders decided
   * and is not a pricing seam; the refusal belongs in the builder, where the
   * customer is standing and the spot can be named in their own words. What
   * the module CAN do is answer the question identically for both of them, on
   * the properties that are actually leaving for /cart/add.js rather than on
   * whichever store each builder happens to keep.
   *
   * A placement with a location and no production artwork url is a decoration
   * the line was priced for and production cannot make — orders 7543 and 7544
   * on 2026-08-22 (3 hoodies, 12 caps). The counterpart defect, artwork with no
   * placement, is refused upstream in both builders and never reaches here.
   *
   * `artwork_status` is honoured because a customer who was told "send it
   * later" was promised exactly that. Nothing sets it since 2026-08-18
   * (scripts/send-logo-later-REMOVED-2026-08-18.md).
   *
   * @param {Object} props  a normalizeOrderItem() result
   * @returns {Array<{index:number, key:string, location:string}>} in line order
   */
  function placementsWithoutArtwork(props) {
    var p = props || {};
    if (p['artwork_status']) return [];
    var count = parseInt(p['placement_count'], 10);
    if (!isFinite(count) || count <= 0) return [];
    var out = [];
    for (var i = 1; i <= count; i++) {
      var prefix = 'placement_' + i + '_';
      if (isBlank(p[prefix + 'location'])) continue;
      if (!isBlank(p[prefix + 'production_artwork_url'])) continue;
      if (!isBlank(p[prefix + 'original_artwork_url'])) continue;
      out.push({ index: i, key: p[prefix + 'key'] || '', location: p[prefix + 'location'] });
    }
    return out;
  }

  var api = {
    LOCATION_NAMES: LOCATION_NAMES,
    placementsWithoutArtwork: placementsWithoutArtwork,
    canonicalLocation: canonicalLocation,
    canonicalMethod: canonicalMethod,
    canonicalView: canonicalView,
    normalizeOrderItem: normalizeOrderItem
  };

  if (root) root.forteeOrderSchema = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  // `globalThis`, not `this`. Under a CommonJS `require` the top-level `this`
  // is module.exports, so `root.console` would be undefined and every warning
  // above would vanish — including the ones the guards exist to raise.
})(typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : this));
